import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spinner, Alert, Badge, ProgressBar } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { fetchLivraisonsJours } from "./DashboardService";

/**
 * Livraisons d'aujourd'hui et du prochain jour de livraison.
 *
 * Tout est compté au serveur (LivraisonsDuJourService) : mêmes exclusions que le
 * reste du dashboard (annulées, paiements non acquis), et règle unique du retard.
 * Les statuts se répartissent sans reste : leur somme fait le total.
 */

// Deux minutes, comme les autres cartons du moment.
const RAFRAICHISSEMENT_MS = 2 * 60 * 1000;

const Compteur = ({ libelle, valeur, couleur }) => (
  <Col xs={6} md={3} className="text-center mb-2">
    <div style={{ fontSize: 22, fontWeight: 700, color: couleur }}>{valeur}</div>
    <div className="text-muted" style={{ fontSize: 12 }}>{libelle}</div>
  </Col>
);

const CarteJour = ({ titre, jour, aujourdhui, onClick }) => (
  <Card className="shadow-sm h-100" role="button" onClick={onClick} style={{ cursor: "pointer" }}>
    <Card.Body>
      <div className="d-flex justify-content-between align-items-baseline mb-2">
        <div>
          <span className="text-uppercase text-muted" style={{ fontSize: 12, fontWeight: 600, letterSpacing: ".04em" }}>
            {titre} · {jour.libelle}
          </span>
        </div>
        <div className="text-end">
          <span style={{ fontSize: 20, fontWeight: 700 }}>{jour.total}</span>{" "}
          <span className="text-muted" style={{ fontSize: 13 }}>
            livraison{jour.total > 1 ? "s" : ""}
          </span>
          {/* Prévision de fin de journée (PrevisionLivraisonsService) : rien
              quand il n'arrive plus rien, ou faute d'historique. */}
          {jour.prevision && jour.prevision.prevues > jour.total && (
            <div className="text-muted" style={{ fontSize: 12 }} title={jour.prevision.explication}>
              ≈ <strong>{jour.prevision.prevues}</strong> prévues
              {jour.prevision.max > jour.prevision.min && ` (${jour.prevision.min} – ${jour.prevision.max})`}
            </div>
          )}
        </div>
      </div>

      <Row>
        <Compteur libelle="à valider" valeur={jour.a_valider} couleur="#0d6efd" />
        <Compteur libelle="en préparation" valeur={jour.en_preparation} couleur="#0dcaf0" />
        <Compteur libelle="en livraison" valeur={jour.en_livraison} couleur="#fd7e14" />
        <Compteur libelle="livrées" valeur={jour.livrees} couleur="#198754" />
      </Row>

      {aujourdhui && jour.total > 0 && (
        <ProgressBar
          now={(jour.livrees / jour.total) * 100}
          variant="success"
          style={{ height: 6 }}
          className="mt-1"
          title={`${jour.livrees} livrée(s) sur ${jour.total}`}
        />
      )}

      <div className="mt-2 d-flex flex-wrap gap-2" style={{ fontSize: 12 }}>
        {jour.en_retard > 0 && (
          <Badge bg="danger" title="Non livrées, échéance dépassée (fin de créneau + manutention prévue)">
            {jour.en_retard} en retard
          </Badge>
        )}
        {jour.sans_livreur > 0 && (
          <Badge bg="secondary" title="Pas encore livrées et sans livreur assigné">
            {jour.sans_livreur} sans livreur
          </Badge>
        )}
        {jour.deux_livreurs > 0 && (
          <Badge bg="warning" text="dark" title="Colis de plus de 40 kg : deux porteurs">
            👥 {jour.deux_livreurs} colis &gt; 40 kg (2 livreurs)
          </Badge>
        )}
        {jour.etage_2_plus > 0 && (
          <Badge bg="danger" title="Portage au 2e étage ou plus, sans ascenseur">
            🪜 {jour.etage_2_plus} au 2e+ sans asc.
          </Badge>
        )}
        {jour.etage_1 > 0 && (
          <Badge bg="warning" text="dark" title="Portage au 1er étage, sans ascenseur">
            🪜 {jour.etage_1} au 1er sans asc.
          </Badge>
        )}
        {jour.etage_ascenseur > 0 && (
          <Badge bg="info" text="dark" title="Portage en étage, avec ascenseur">
            🛗 {jour.etage_ascenseur} en étage avec asc.
          </Badge>
        )}
        {jour.avec_manutention > 0 && (
          <Badge bg="light" text="dark" className="border" title="Manutention commandée ou à confirmer">
            🏋️ {jour.avec_manutention} avec manutention
          </Badge>
        )}
      </div>
    </Card.Body>
  </Card>
);

const LivraisonsJours = () => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let annule = false;

    const charger = async () => {
      try {
        const resultat = await fetchLivraisonsJours();
        if (!annule) {
          setData(resultat);
          setError(null);
        }
      } catch (e) {
        console.error("Erreur chargement livraisons du jour:", e);
        if (!annule) setError("Impossible de charger les livraisons du jour");
      }
    };

    charger();
    const interval = setInterval(charger, RAFRAICHISSEMENT_MS);

    return () => {
      annule = true;
      clearInterval(interval);
    };
  }, []);

  if (error) {
    return <Alert variant="warning" className="py-2 mb-0">{error}</Alert>;
  }

  if (!data) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  // La liste des livraisons, filtrée sur le jour de la carte.
  const ouvrirLivraisons = (date) => history.push(`/livraison?date=${date}`);

  // « Demain » si le prochain jour livrable est bien demain, sinon « Prochain
  // jour » : le samedi, la carte montre le lundi, et sa date le dit.
  const demain = new Date();
  demain.setDate(demain.getDate() + 1);
  const demainIso = `${demain.getFullYear()}-${String(demain.getMonth() + 1).padStart(2, "0")}-${String(demain.getDate()).padStart(2, "0")}`;
  const titreSuivant = data.demain.date === demainIso ? "Demain" : "Prochain jour";

  return (
    <Row>
      <Col md={6} className="mb-3">
        <CarteJour titre="Aujourd'hui" jour={data.aujourdhui} aujourdhui onClick={() => ouvrirLivraisons(data.aujourdhui.date)} />
      </Col>
      <Col md={6} className="mb-3">
        <CarteJour titre={titreSuivant} jour={data.demain} onClick={() => ouvrirLivraisons(data.demain.date)} />
      </Col>
    </Row>
  );
};

export default LivraisonsJours;
